import axios from "axios";
import { FIDO_API_URL } from "../config";

/** WebAuthn ceremony timeout — USB keys with PIN need extra time. */
export const WEBAUTHN_TIMEOUT_MS = 300_000;

export const WEBAUTHN_TIMEOUT_MINUTES = WEBAUTHN_TIMEOUT_MS / 60_000;

export type WebAuthnErrorCode =
  | "timeout"
  | "cancelled"
  | "aborted"
  | "no_authenticator"
  | "security"
  | "unsupported"
  | "server_config"
  | "already_registered"
  | "retryable"
  | "unknown";

const WEBAUTHN_TIMEOUT_HINT = `Você tem até ${WEBAUTHN_TIMEOUT_MINUTES} minutos: digite o PIN na chave USB e toque no botão quando o navegador solicitar.`;

const WEBAUTHN_TIMEOUT_MESSAGE = `Tempo esgotado. ${WEBAUTHN_TIMEOUT_HINT}`;

const WEBAUTHN_CANCELLED_MESSAGE =
  "Operação cancelada. Se estava digitando o PIN, tente novamente — " +
  WEBAUTHN_TIMEOUT_HINT.toLowerCase();

const WEBAUTHN_ABORTED_MESSAGE =
  "A operação foi interrompida. Clique em tentar novamente e conclua o PIN e o toque na chave sem fechar esta janela.";

export const WEBAUTHN_USB_REQUIRED_MESSAGE =
  "Chave USB deve estar conectada. Conecte a chave antes de clicar.";

const WEBAUTHN_NO_AUTHENTICATOR_MESSAGE =
  "Conecte a chave USB. Nenhuma chave física detectada — use o Google Chrome e acesse sempre pelo mesmo endereço (localhost ou 127.0.0.1 — não misture).";

const WEBAUTHN_WINDOWS_HINT =
  "No Windows, o diálogo de segurança pode abrir atrás do navegador — use Alt+Tab para encontrá-lo e conclua o PIN e o toque na chave.";

const WEBAUTHN_TROUBLESHOOTING_HINT =
  `Dicas: use Google Chrome, mantenha a chave conectada antes de iniciar, acesse sempre o mesmo endereço (ex.: só https://localhost, nunca http://192.168.x.x), e conclua o PIN quando o navegador solicitar. ${WEBAUTHN_WINDOWS_HINT}`;

export class WebAuthnError extends Error {
  code?: WebAuthnErrorCode;

  constructor(message: string, code?: WebAuthnErrorCode) {
    super(message);
    this.name = "WebAuthnError";
    this.code = code;
  }
}

const isDev = (): boolean => process.env.NODE_ENV === "development";

const isMockMode = (): boolean =>
  process.env.REACT_APP_FIDO_MOCK === "true";

/** Chrome often returns this ambiguous message for cancel, missing key, or timeout. */
const isChromeAmbiguousNotAllowed = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes("timed out or was not allowed") ||
    lower.includes("either timed out or was not allowed")
  );
};

const isStrictTimeoutMessage = (message: string): boolean => {
  const lower = message.toLowerCase();
  if (isChromeAmbiguousNotAllowed(message)) return false;
  return (
    lower.includes("timeout") ||
    lower.includes("timed out") ||
    lower.includes("time limit") ||
    lower.includes("tempo esgotado") ||
    lower.includes("time exceeded")
  );
};

/** Browser WebAuthn errors only — avoid matching HTTP/API "not found" strings. */
const isNoAuthenticatorMessage = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes("no authenticator") ||
    lower.includes("no credentials") ||
    lower.includes("cannot find") ||
    lower.includes("device not found") ||
    lower.includes("authenticator not found")
  );
};

const LOCAL_WEBAUTHN_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

const isPrivateLanHostname = (hostname: string): boolean => {
  if (LOCAL_WEBAUTHN_HOSTNAMES.has(hostname)) return false;

  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;

  const match172 = hostname.match(/^172\.(\d{1,2})\.\d{1,3}\.\d{1,3}$/);
  if (match172) {
    const secondOctet = Number(match172[1]);
    return secondOctet >= 16 && secondOctet <= 31;
  }

  return false;
};

export type WebAuthnUnavailableReason =
  | "insecure_context"
  | "unsupported_browser"
  | null;

export interface WebAuthnEnvironmentDebug {
  href: string;
  origin: string;
  hostname: string;
  port: string;
  isSecureContext: boolean;
  hasPublicKeyCredential: boolean;
  hasCredentials: boolean;
  unavailableReason: WebAuthnUnavailableReason;
  fidoMock: boolean;
  fidoApiUrl: string;
}

let environmentLogged = false;

/** Dev-only snapshot of tab context relevant to WebAuthn. */
export const getWebAuthnEnvironmentDebug = (): WebAuthnEnvironmentDebug => {
  if (typeof window === "undefined") {
    return {
      href: "",
      origin: "",
      hostname: "",
      port: "",
      isSecureContext: false,
      hasPublicKeyCredential: false,
      hasCredentials: false,
      unavailableReason: "unsupported_browser",
      fidoMock: isMockMode(),
      fidoApiUrl: FIDO_API_URL,
    };
  }

  return {
    href: window.location.href,
    origin: window.location.origin,
    hostname: window.location.hostname,
    port: window.location.port,
    isSecureContext: window.isSecureContext,
    hasPublicKeyCredential: Boolean(window.PublicKeyCredential),
    hasCredentials: Boolean(navigator.credentials),
    unavailableReason: getWebAuthnUnavailableReason(),
    fidoMock: isMockMode(),
    fidoApiUrl: FIDO_API_URL,
  };
};

/** Logs environment once per page load in development. */
export const logWebAuthnEnvironment = (): void => {
  if (!isDev() || environmentLogged || typeof window === "undefined") return;
  environmentLogged = true;
  console.info("[WebAuthn:environment]", getWebAuthnEnvironmentDebug());
};

/** True when this browser/context can run WebAuthn ceremonies. */
export const isWebAuthnAvailable = (): boolean => {
  logWebAuthnEnvironment();
  return getWebAuthnUnavailableReason() === null;
};

/** Why WebAuthn is blocked in the current tab (secure context, browser API, etc.). */
export const getWebAuthnUnavailableReason = (): WebAuthnUnavailableReason => {
  if (typeof window === "undefined") return "unsupported_browser";

  if (!window.isSecureContext) {
    return "insecure_context";
  }

  if (!window.PublicKeyCredential || !navigator.credentials) {
    return "unsupported_browser";
  }

  return null;
};

const LOCAL_DEV_PORT_HINT = "http://localhost:3000 (porta 3000, não 300)";

/** User-facing explanation when WebAuthn cannot run in this tab. */
export const getWebAuthnUnavailableMessage = (): string => {
  const reason = getWebAuthnUnavailableReason();
  const { hostname, port, href } = window.location;
  const devPort = port ? `:${port}` : "";

  if (reason === "insecure_context") {
    if (isPrivateLanHostname(hostname)) {
      return (
        `Chaves USB (WebAuthn) não funcionam em http://${hostname}${devPort}. ` +
        `Abra o app em ${LOCAL_DEV_PORT_HINT} ou http://127.0.0.1:3000 no Google Chrome ` +
        "(não use o IP da rede local)."
      );
    }

    return (
      `WebAuthn exige contexto seguro. Endereço atual: ${href}. ` +
      `Abra ${LOCAL_DEV_PORT_HINT} ou http://127.0.0.1:3000 no Google Chrome ` +
      "(não use o nome do PC nem IP da rede)."
    );
  }

  if (reason === "unsupported_browser") {
    const missingApi =
      !window.PublicKeyCredential && !navigator.credentials
        ? "PublicKeyCredential e navigator.credentials ausentes"
        : !window.PublicKeyCredential
          ? "PublicKeyCredential ausente"
          : "navigator.credentials ausente";

    if (isDev()) {
      console.warn("[WebAuthn:environment] unsupported_browser —", missingApi, {
        href,
        userAgent: navigator.userAgent,
      });
    }

    return (
      "Seu navegador não suporta chaves de segurança (WebAuthn) nesta janela. " +
      `Use Google Chrome atualizado em ${LOCAL_DEV_PORT_HINT} — não use o navegador embutido do IDE.`
    );
  }

  return "";
};

const assertWebAuthnAvailable = (): void => {
  const reason = getWebAuthnUnavailableReason();
  if (!reason) return;

  throw new WebAuthnError(
    getWebAuthnUnavailableMessage(),
    reason === "insecure_context" ? "security" : "unsupported"
  );
};

export interface WebAuthnErrorDetails {
  code: WebAuthnErrorCode;
  message: string;
  troubleshooting?: string;
}

const extractApiErrorMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined;
  const row = data as Record<string, unknown>;

  const information =
    (typeof row.Information === "string" && row.Information) ||
    (typeof row.information === "string" && row.information) ||
    undefined;

  const description =
    (typeof row.Description === "string" && row.Description) ||
    (typeof row.description === "string" && row.description) ||
    undefined;

  // Backend puts the actionable Fido2/parse detail in `information`.
  if (information && description && information !== description) {
    return information;
  }

  return information || description || undefined;
};

/** Maps FIDO API (axios) failures — never "browser unsupported". */
const classifyApiError = (error: unknown): WebAuthnErrorDetails | null => {
  if (!axios.isAxiosError(error)) return null;

  const status = error.response?.status;
  const apiMessage = extractApiErrorMessage(error.response?.data);

  if (isDev()) {
    console.error("[WebAuthn:api]", {
      url: error.config?.url,
      method: error.config?.method,
      status,
      data: error.response?.data,
      message: error.message,
    });
  }

  if (!error.response) {
    return {
      code: "unknown",
      message:
        "Não foi possível conectar ao servidor WebAuthn (API FIDO). " +
        "Verifique se o mobile-service está em execução (local: http://localhost:44352 no profile http). " +
        "Se usar https://localhost:44352, aceite o certificado autoassinado no Chrome antes de tentar de novo.",
      troubleshooting:
        "Isso é erro de rede/certificado SSL na API, não falta de suporte WebAuthn no navegador.",
    };
  }

  if (status === 401) {
    return {
      code: "unknown",
      message:
        apiMessage ||
        "Sessão expirada. Faça login novamente antes de cadastrar ou usar a chave USB.",
    };
  }

  if (status === 404) {
    return {
      code: "unknown",
      message:
        apiMessage ||
        "Nenhuma credencial WebAuthn encontrada nesta conta. Cadastre a chave USB nas configurações primeiro.",
    };
  }

  return {
    code: "unknown",
    message:
      apiMessage ||
      `Erro do servidor WebAuthn (HTTP ${status ?? "?"}). Tente novamente ou use SMS/WhatsApp.`,
  };
};

const isServerConfigNotSupportedError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes("required parameters missing") ||
    lower.includes("required parameter") ||
    lower.includes("missing required")
  );
};

export const classifyWebAuthnError = (error: unknown): WebAuthnErrorDetails => {
  const apiError = classifyApiError(error);
  if (apiError) return apiError;

  if (error instanceof WebAuthnError) {
    return {
      code: error.code ?? "unknown",
      message: error.message,
      troubleshooting:
        error.code === "unknown" ||
        error.code === "no_authenticator" ||
        error.code === "security" ||
        error.code === "unsupported" ||
        error.code === "server_config"
          ? error.code === "server_config"
            ? "O servidor enviou opções de registro/autenticação inválidas (campos binários ou obrigatórios ausentes)."
            : WEBAUTHN_TROUBLESHOOTING_HINT
          : undefined,
    };
  }

  if (error instanceof DOMException) {
    const rawMessage = error.message ?? "";

    switch (error.name) {
      case "NotAllowedError": {
        if (isStrictTimeoutMessage(rawMessage)) {
          return { code: "timeout", message: WEBAUTHN_TIMEOUT_MESSAGE };
        }
        if (isNoAuthenticatorMessage(rawMessage)) {
          return {
            code: "no_authenticator",
            message: WEBAUTHN_NO_AUTHENTICATOR_MESSAGE,
            troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
          };
        }
        if (isChromeAmbiguousNotAllowed(rawMessage)) {
          return {
            code: "retryable",
            message:
              `${WEBAUTHN_USB_REQUIRED_MESSAGE} Verifique o PIN e o toque no botão da chave física — não use Windows Hello. ` +
              WEBAUTHN_WINDOWS_HINT,
            troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
          };
        }
        return { code: "cancelled", message: WEBAUTHN_CANCELLED_MESSAGE };
      }
      case "AbortError":
        return { code: "aborted", message: WEBAUTHN_ABORTED_MESSAGE };
      case "SecurityError":
        return {
          code: "security",
          message: getWebAuthnUnavailableMessage() ||
            "WebAuthn não está disponível neste contexto. Use HTTPS ou localhost e mantenha o mesmo endereço (localhost vs 127.0.0.1).",
          troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
        };
      case "NotSupportedError": {
        if (isServerConfigNotSupportedError(rawMessage)) {
          return {
            code: "server_config",
            message:
              "Erro na configuração WebAuthn do servidor. Tente novamente ou use SMS/WhatsApp.",
            troubleshooting:
              "O servidor enviou opções de registro/autenticação inválidas (campos binários ou obrigatórios ausentes).",
          };
        }
        return {
          code: "unsupported",
          message: "Seu navegador ou dispositivo não suporta chaves de segurança.",
          troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
        };
      }
      case "InvalidStateError":
        return {
          code: "already_registered",
          message: "Esta chave já está cadastrada neste dispositivo.",
        };
      default:
        if (isStrictTimeoutMessage(rawMessage)) {
          return { code: "timeout", message: WEBAUTHN_TIMEOUT_MESSAGE };
        }
        return {
          code: "unknown",
          message: WEBAUTHN_USB_REQUIRED_MESSAGE,
          troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
        };
    }
  }

  if (error instanceof Error) {
    if (isStrictTimeoutMessage(error.message)) {
      return { code: "timeout", message: WEBAUTHN_TIMEOUT_MESSAGE };
    }
    return {
      code: "unknown",
      message: error.message,
      troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
    };
  }

  return {
    code: "unknown",
    message: "Não foi possível verificar a chave USB. Tente novamente.",
    troubleshooting: WEBAUTHN_TROUBLESHOOTING_HINT,
  };
};

export const mapWebAuthnError = (error: unknown): string =>
  classifyWebAuthnError(error).message;

export const isWebAuthnRetryableError = (error: unknown): boolean => {
  const { code } = classifyWebAuthnError(error);
  return (
    code === "timeout" ||
    code === "retryable" ||
    code === "cancelled" ||
    code === "aborted" ||
    code === "no_authenticator"
  );
};

export const logWebAuthnError = (
  ceremony: "register" | "authenticate",
  error: unknown
): void => {
  if (!isDev()) return;

  const classified = classifyWebAuthnError(error);

  if (error instanceof DOMException) {
    console.error(`[WebAuthn:${ceremony}]`, {
      name: error.name,
      message: error.message,
      code: error.code,
      classifiedCode: classified.code,
      classifiedMessage: classified.message,
      rpId: window.location.hostname,
      origin: window.location.origin,
    });
    return;
  }

  console.error(`[WebAuthn:${ceremony}]`, error, classified);
};

export const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const base64urlToBuffer = (base64url: string): ArrayBuffer => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const normalizeStoredCredentialId = (id: string): string =>
  bufferToBase64url(base64urlToBuffer(id.replace(/\+/g, "-").replace(/\//g, "_")));

const randomChallenge = (): Uint8Array => {
  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);
  return challenge;
};

const getWebAuthnRpId = (): string => window.location.hostname;

/** Resolves rpId from server rp.id, top-level rpId, or current hostname. */
const resolveRpId = (
  raw: Record<string, unknown>,
  rpRaw?: Record<string, unknown>
): string => {
  const candidates = [rpRaw?.id, raw.rpId];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return getWebAuthnRpId();
};

/** Fido2NetLib may serialize PublicKeyCredentialType as numeric enum (0 = public-key). */
const normalizeCredentialType = (type: unknown): PublicKeyCredentialType => {
  if (type === "public-key" || type === 0 || type === "0") return "public-key";
  if (typeof type === "string" && type.length > 0) {
    return type as PublicKeyCredentialType;
  }
  return "public-key";
};

/** WebAuthn L3 — Chrome 128+ prefers physical security keys over platform passkeys. */
type PublicKeyCredentialHint = "security-key" | "client-device" | "hybrid";

type SecurityKeyCreationOptions = PublicKeyCredentialCreationOptions & {
  hints?: PublicKeyCredentialHint[];
};

type SecurityKeyRequestOptions = PublicKeyCredentialRequestOptions & {
  hints?: PublicKeyCredentialHint[];
};

let activeCeremony: "register" | "authenticate" | null = null;

const acquireCeremony = (type: "register" | "authenticate"): void => {
  if (activeCeremony) {
    throw new WebAuthnError(
      "Já existe uma operação de chave USB em andamento. Aguarde e tente novamente.",
      "aborted"
    );
  }
  activeCeremony = type;
};

const releaseCeremony = (): void => {
  activeCeremony = null;
};

const USB_TRANSPORTS: AuthenticatorTransport[] = ["usb"];

const resolveUsbTransports = (transports?: string[]): AuthenticatorTransport[] => {
  if (!transports?.length) return USB_TRANSPORTS;
  const usbOnly = transports.filter((t) => t === "usb") as AuthenticatorTransport[];
  return usbOnly.length ? usbOnly : USB_TRANSPORTS;
};

const assertCrossPlatformCredential = (
  credential: PublicKeyCredential,
  transports: string[]
): void => {
  if (transports.includes("internal")) {
    throw new WebAuthnError(
      "Esta credencial pertence ao Windows Hello, não à chave USB. Remova-a e cadastre novamente com a chave física conectada.",
      "security"
    );
  }
};

export interface RegisterSecurityKeyParams {
  userId: string;
  username: string;
  label?: string;
  registrationAttempt?: number;
}

export interface RegisterSecurityKeyResult {
  credentialId: string;
  transports: string[];
}

const encodeUserId = (userId: string): Uint8Array => {
  const normalized = userId.trim() || "anonymous";
  const bytes = new TextEncoder().encode(normalized);
  if (bytes.length === 0) {
    throw new WebAuthnError("ID de usuário inválido para WebAuthn.", "unknown");
  }
  if (bytes.length > 64) {
    return bytes.slice(0, 64);
  }
  return bytes;
};

const buildRegisterOptions = (
  userIdBytes: Uint8Array,
  displayName: string,
  rpId: string,
  registrationAttempt: number
): SecurityKeyCreationOptions => {
  const strictUsbOnly = registrationAttempt > 1;
  const authenticatorSelection: AuthenticatorSelectionCriteria = {
    userVerification: "discouraged",
    residentKey: "discouraged",
    requireResidentKey: false,
  };

  if (strictUsbOnly) {
    authenticatorSelection.authenticatorAttachment = "cross-platform";
  }

  return {
    challenge: randomChallenge(),
    rp: {
      name: "VALUT",
      id: rpId,
    },
    user: {
      id: userIdBytes,
      name: displayName,
      displayName,
    },
    pubKeyCredParams: [
      { type: "public-key", alg: -7 },
      { type: "public-key", alg: -257 },
    ],
    authenticatorSelection,
    timeout: WEBAUTHN_TIMEOUT_MS,
    attestation: "none",
    hints: ["security-key"],
  };
};

/** Maps Fido2NetLib / ASP.NET JSON keys (PascalCase) to Web API camelCase. */
const toCamelCaseKey = (key: string): string => {
  if (!key) return key;
  return key.charAt(0).toLowerCase() + key.slice(1);
};

const normalizeServerJson = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeServerJson);
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  const normalized: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    normalized[toCamelCaseKey(key)] = normalizeServerJson(nested);
  }
  return normalized;
};

const CEREMONY_OPTION_WRAPPERS = [
  "publicKey",
  "options",
  "publicKeyCredentialCreationOptions",
  "publicKeyCredentialRequestOptions",
  "credentialCreateOptions",
  "credentialRequestOptions",
  "assertionOptions",
  "attestationOptions",
] as const;

/** Unwraps nested API payloads into a flat ceremony options object. */
const unwrapCeremonyOptions = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== "object") {
    throw new WebAuthnError(
      "Resposta WebAuthn inválida do servidor.",
      "server_config"
    );
  }

  let current = normalizeServerJson(payload) as Record<string, unknown>;

  for (let depth = 0; depth < 4; depth += 1) {
    const wrapperKey = CEREMONY_OPTION_WRAPPERS.find(
      (key) => current[key] && typeof current[key] === "object"
    );
    if (!wrapperKey) break;
    current = normalizeServerJson(current[wrapperKey]) as Record<string, unknown>;
  }

  return current;
};

const decodeBinaryField = (
  value: unknown,
  fieldName: string
): ArrayBuffer => {
  if (value instanceof ArrayBuffer) return value;
  if (value instanceof Uint8Array) {
    return value.buffer.slice(
      value.byteOffset,
      value.byteOffset + value.byteLength
    );
  }
  if (Array.isArray(value) && value.every((item) => typeof item === "number")) {
    return new Uint8Array(value as number[]).buffer;
  }
  if (typeof value === "string" && value.length > 0) {
    return base64urlToBuffer(value);
  }

  throw new WebAuthnError(
    `Erro na configuração WebAuthn do servidor: campo "${fieldName}" inválido ou ausente.`,
    "server_config"
  );
};

const decodeCredentialDescriptor = (
  cred: Record<string, unknown>
): PublicKeyCredentialDescriptor => {
  const id = cred.id ?? cred.rawId;
  const transports = cred.transports;

  return {
    type: (typeof cred.type === "string" ? cred.type : "public-key") as PublicKeyCredentialType,
    id: decodeBinaryField(id, "credential.id"),
    ...(Array.isArray(transports)
      ? { transports: transports as AuthenticatorTransport[] }
      : {}),
  };
};

const describeBinaryField = (value: unknown): string => {
  if (value instanceof ArrayBuffer) return `ArrayBuffer(${value.byteLength})`;
  if (value instanceof Uint8Array) return `Uint8Array(${value.byteLength})`;
  if (typeof value === "string") return `string(${value.length})`;
  if (Array.isArray(value)) return `number[${value.length}]`;
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  return typeof value;
};

const logDecodedCeremonyOptions = (
  ceremony: "register" | "authenticate",
  publicKey: SecurityKeyCreationOptions | SecurityKeyRequestOptions
): void => {
  if (!isDev()) return;

  const creationOptions = publicKey as SecurityKeyCreationOptions;
  const requestOptions = publicKey as SecurityKeyRequestOptions;

  const registrationRpId = creationOptions.rp?.id;
  const authenticationRpId = requestOptions.rpId ?? registrationRpId;

  console.info(`[WebAuthn:${ceremony}] decoded options`, {
    keys: Object.keys(publicKey),
    challenge: describeBinaryField(publicKey.challenge),
    rp: creationOptions.rp,
    rpId: authenticationRpId,
    user: creationOptions.user
      ? {
          ...creationOptions.user,
          id: describeBinaryField(creationOptions.user.id),
          idType:
            creationOptions.user.id instanceof ArrayBuffer
              ? "ArrayBuffer"
              : creationOptions.user.id?.constructor?.name ?? typeof creationOptions.user.id,
        }
      : undefined,
    pubKeyCredParams: creationOptions.pubKeyCredParams?.length ?? 0,
    authenticatorSelection: creationOptions.authenticatorSelection,
    excludeCredentials: creationOptions.excludeCredentials?.map((cred) => ({
      type: cred.type,
      id: describeBinaryField(cred.id),
      transports: cred.transports,
    })),
    allowCredentials: requestOptions.allowCredentials?.map((cred) => ({
      type: cred.type,
      id: describeBinaryField(cred.id),
      transports: cred.transports,
    })),
    timeout: publicKey.timeout,
    userVerification:
      creationOptions.authenticatorSelection?.userVerification ??
      requestOptions.userVerification,
  });
};

/**
 * Decodes server-sent options where binary fields arrive as base64url strings
 * into proper ArrayBuffer values expected by navigator.credentials.create().
 */
const decodeCreationOptions = (
  serverOptions: unknown
): SecurityKeyCreationOptions => {
  const raw = unwrapCeremonyOptions(serverOptions);

  const challenge = decodeBinaryField(raw.challenge, "challenge");
  const rpRaw = raw.rp as Record<string, unknown> | undefined;
  const userRaw = raw.user as Record<string, unknown> | undefined;

  if (!userRaw || typeof userRaw !== "object") {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: user ausente.",
      "server_config"
    );
  }

  const rpId = resolveRpId(raw, rpRaw);
  const rp = {
    id: rpId,
    name: String(rpRaw?.name ?? "VALUT"),
  };

  const userId = decodeBinaryField(userRaw.id, "user.id");
  const userName = String(userRaw.name ?? userRaw.displayName ?? "Usuário");
  const userDisplayName = String(userRaw.displayName ?? userRaw.name ?? userName);

  const pubKeyCredParams = Array.isArray(raw.pubKeyCredParams)
    ? (raw.pubKeyCredParams as Array<{ type?: unknown; alg?: number }>).map(
        (param) => ({
          type: normalizeCredentialType(param.type),
          alg: Number(param.alg),
        })
      )
    : undefined;

  if (!pubKeyCredParams?.length || pubKeyCredParams.some((p) => !Number.isFinite(p.alg))) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: pubKeyCredParams ausente.",
      "server_config"
    );
  }

  const authenticatorSelectionRaw = raw.authenticatorSelection as
    | Record<string, unknown>
    | undefined;

  const publicKey: SecurityKeyCreationOptions = {
    challenge,
    rp,
    user: {
      id: userId,
      name: userName,
      displayName: userDisplayName,
    },
    pubKeyCredParams,
    ...(authenticatorSelectionRaw
      ? {
          authenticatorSelection: {
            ...(typeof authenticatorSelectionRaw.authenticatorAttachment === "string"
              ? {
                  authenticatorAttachment:
                    authenticatorSelectionRaw.authenticatorAttachment as AuthenticatorAttachment,
                }
              : {}),
            ...(typeof authenticatorSelectionRaw.userVerification === "string"
              ? {
                  userVerification:
                    authenticatorSelectionRaw.userVerification as UserVerificationRequirement,
                }
              : {}),
            ...(typeof authenticatorSelectionRaw.residentKey === "string"
              ? {
                  residentKey:
                    authenticatorSelectionRaw.residentKey as ResidentKeyRequirement,
                }
              : {}),
            ...(typeof authenticatorSelectionRaw.requireResidentKey === "boolean"
              ? { requireResidentKey: authenticatorSelectionRaw.requireResidentKey }
              : {}),
          },
        }
      : {}),
    ...(Array.isArray(raw.excludeCredentials)
      ? {
          excludeCredentials: (raw.excludeCredentials as Record<string, unknown>[]).map(
            decodeCredentialDescriptor
          ),
        }
      : {}),
    ...(typeof raw.timeout === "number" ? { timeout: raw.timeout } : {}),
    ...(typeof raw.attestation === "string"
      ? { attestation: raw.attestation as AttestationConveyancePreference }
      : {}),
  };

  return publicKey;
};

/** Ensures decoded creation options satisfy WebAuthn required fields before ceremony. */
const assertCreationOptionsValid = (publicKey: SecurityKeyCreationOptions): void => {
  if (!(publicKey.challenge instanceof ArrayBuffer)) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: challenge inválido.",
      "server_config"
    );
  }
  if (!publicKey.rp?.id?.trim() || !publicKey.rp?.name?.trim()) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: rp.id ou rp.name ausente.",
      "server_config"
    );
  }
  if (!(publicKey.user?.id instanceof ArrayBuffer)) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: user.id deve ser ArrayBuffer.",
      "server_config"
    );
  }
  if (
    typeof publicKey.user.name !== "string" ||
    typeof publicKey.user.displayName !== "string"
  ) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: user.name ou user.displayName inválido.",
      "server_config"
    );
  }
  if (!publicKey.pubKeyCredParams?.length) {
    throw new WebAuthnError(
      "Erro na configuração WebAuthn do servidor: pubKeyCredParams ausente.",
      "server_config"
    );
  }
};

/**
 * Decodes server-sent assertion options where binary fields arrive as base64url strings.
 */
const decodeRequestOptions = (
  serverOptions: unknown
): SecurityKeyRequestOptions => {
  const raw = unwrapCeremonyOptions(serverOptions);

  const publicKey: SecurityKeyRequestOptions = {
    challenge: decodeBinaryField(raw.challenge, "challenge"),
    rpId: resolveRpId(raw),
    ...(Array.isArray(raw.allowCredentials)
      ? {
          allowCredentials: (raw.allowCredentials as Record<string, unknown>[]).map(
            decodeCredentialDescriptor
          ),
        }
      : {}),
    ...(typeof raw.timeout === "number" ? { timeout: raw.timeout } : {}),
    ...(typeof raw.userVerification === "string"
      ? {
          userVerification: raw.userVerification as UserVerificationRequirement,
        }
      : {}),
  };

  return publicKey;
};

/**
 * Serializes an AuthenticatorAttestationResponse for Fido2NetLib (System.Text.Json).
 * Property names must stay camelCase with `clientDataJSON` (capital JSON), not `clientDataJson`.
 */
export const serializeAttestationResponse = (credential: PublicKeyCredential) => {
  const response = credential.response as AuthenticatorAttestationResponse;
  const rawTransports = response.getTransports?.() ?? [];
  const transports = rawTransports.length ? rawTransports : (["usb"] as AuthenticatorTransport[]);
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      transports,
    },
    clientExtensionResults: (credential.getClientExtensionResults?.() ??
      {}) as Record<string, unknown>,
  };
};

/**
 * Serializes an AuthenticatorAssertionResponse to a JSON-safe payload for the backend.
 */
export const serializeAssertionResponse = (assertion: PublicKeyCredential) => {
  const response = assertion.response as AuthenticatorAssertionResponse;
  return {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64url(response.userHandle)
        : undefined,
    },
    clientExtensionResults: (assertion.getClientExtensionResults?.() ??
      {}) as Record<string, unknown>,
  };
};

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export const registerSecurityKey = async ({
  userId,
  username,
  label,
  registrationAttempt = 1,
}: RegisterSecurityKeyParams): Promise<RegisterSecurityKeyResult> => {
  assertWebAuthnAvailable();
  acquireCeremony("register");

  try {
    if (!isMockMode()) {
      return await registerSecurityKeyRemote({ userId, username, label, registrationAttempt });
    }
    return await registerSecurityKeyLocal({ userId, username, registrationAttempt });
  } finally {
    releaseCeremony();
  }
};

/** Server-backed registration: fetch options from API, create credential, verify on server. */
const registerSecurityKeyRemote = async ({
  label,
  registrationAttempt = 1,
}: RegisterSecurityKeyParams): Promise<RegisterSecurityKeyResult> => {
  const { webauthnApi, buildRegisterVerifyPayload } = await import("./webauthnApi");

  const { data: serverOptions } = await webauthnApi.getRegisterOptions();
  const publicKey = decodeCreationOptions(serverOptions);
  assertCreationOptionsValid(publicKey);

  if (registrationAttempt > 1 && publicKey.authenticatorSelection) {
    publicKey.authenticatorSelection.authenticatorAttachment = "cross-platform";
  }
  (publicKey as SecurityKeyCreationOptions).hints = ["security-key"];
  publicKey.timeout = WEBAUTHN_TIMEOUT_MS;

  if (isDev()) {
    console.info("[WebAuthn:register] remote ceremony", {
      rpId: publicKey.rp?.id,
      origin: window.location.origin,
      timeoutMs: WEBAUTHN_TIMEOUT_MS,
      registrationAttempt,
    });
    logDecodedCeremonyOptions("register", publicKey);
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (error) {
    logWebAuthnError("register", error);
    throw error;
  }

  if (!credential) {
    throw new WebAuthnError("Nenhuma credencial foi criada.", "unknown");
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  const rawTransports = response.getTransports?.() ?? ["usb"];
  const transports = rawTransports.length ? [...rawTransports] : ["usb"];

  assertCrossPlatformCredential(credential, transports);

  const attestationPayload = serializeAttestationResponse(credential);

  await webauthnApi.verifyRegistration(
    buildRegisterVerifyPayload(
      attestationPayload,
      label?.trim() || "Chave USB",
      resolveUsbTransports(transports)
    )
  );

  return {
    credentialId: normalizeStoredCredentialId(bufferToBase64url(credential.rawId)),
    transports: resolveUsbTransports(transports),
  };
};

/** Local/mock registration (original flow — generates challenge locally). */
const registerSecurityKeyLocal = async ({
  userId,
  username,
  registrationAttempt = 1,
}: Omit<RegisterSecurityKeyParams, "label">): Promise<RegisterSecurityKeyResult> => {
  const userIdBytes = encodeUserId(userId);
  const rpId = getWebAuthnRpId();
  const displayName = username.trim() || userId.trim() || "Usuário";
  const publicKey = buildRegisterOptions(
    userIdBytes,
    displayName,
    rpId,
    registrationAttempt
  );

  if (isDev()) {
    console.info("[WebAuthn:register] local/mock ceremony", {
      rpId,
      origin: window.location.origin,
      timeoutMs: WEBAUTHN_TIMEOUT_MS,
      registrationAttempt,
      authenticatorAttachment:
        publicKey.authenticatorSelection?.authenticatorAttachment ?? "any",
      userVerification: publicKey.authenticatorSelection?.userVerification,
      residentKey: publicKey.authenticatorSelection?.residentKey,
      hints: publicKey.hints,
    });
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (error) {
    logWebAuthnError("register", error);
    throw error;
  }

  if (!credential) {
    throw new WebAuthnError("Nenhuma credencial foi criada.", "unknown");
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  const rawTransports = response.getTransports?.() ?? ["usb"];
  const transports = rawTransports.length ? [...rawTransports] : ["usb"];

  assertCrossPlatformCredential(credential, transports);

  return {
    credentialId: normalizeStoredCredentialId(bufferToBase64url(credential.rawId)),
    transports: resolveUsbTransports(transports),
  };
};

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

export interface WebAuthnCredentialRef {
  id: string;
  transports?: string[];
}

export interface AuthenticateSecurityKeyParams {
  /** @deprecated pass `credentials` with optional transports per key */
  credentialIds?: string[];
  credentials?: WebAuthnCredentialRef[];
  document?: string;
}

export interface AuthenticateSecurityKeyResult {
  credentialId: string;
  auth?: import("./webauthnApi").WebAuthnTokenModel;
}

export const authenticateSecurityKey = async ({
  credentialIds = [],
  credentials = [],
  document,
}: AuthenticateSecurityKeyParams): Promise<AuthenticateSecurityKeyResult> => {
  assertWebAuthnAvailable();
  acquireCeremony("authenticate");

  try {
    if (!isMockMode() && document) {
      return await authenticateSecurityKeyRemote(document);
    }
    return await authenticateSecurityKeyLocal({ credentialIds, credentials });
  } finally {
    releaseCeremony();
  }
};

/** Server-backed authentication: fetch options, get assertion, verify on server. */
const authenticateSecurityKeyRemote = async (
  document: string
): Promise<AuthenticateSecurityKeyResult> => {
  const { webauthnApi } = await import("./webauthnApi");

  const { data: serverOptions } = await webauthnApi.getAuthOptions(document);
  const publicKey = decodeRequestOptions(serverOptions);
  (publicKey as SecurityKeyRequestOptions).hints = ["security-key"];
  publicKey.timeout = WEBAUTHN_TIMEOUT_MS;

  if (isDev()) {
    console.info("[WebAuthn:authenticate] remote ceremony", {
      rpId: publicKey.rpId,
      origin: window.location.origin,
      timeoutMs: WEBAUTHN_TIMEOUT_MS,
      credentialCount: publicKey.allowCredentials?.length ?? 0,
    });
    logDecodedCeremonyOptions("authenticate", publicKey);
  }

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      mediation: "required",
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (error) {
    logWebAuthnError("authenticate", error);
    throw error;
  }

  if (!assertion) {
    throw new WebAuthnError("Nenhuma credencial foi selecionada.", "unknown");
  }

  const assertionPayload = serializeAssertionResponse(assertion);
  const { data: auth } = await webauthnApi.verifyAuthentication(assertionPayload);

  return {
    credentialId: normalizeStoredCredentialId(bufferToBase64url(assertion.rawId)),
    auth,
  };
};

/** Local/mock authentication (original flow). */
const authenticateSecurityKeyLocal = async ({
  credentialIds = [],
  credentials = [],
}: Omit<AuthenticateSecurityKeyParams, "document">): Promise<AuthenticateSecurityKeyResult> => {
  const refs: WebAuthnCredentialRef[] =
    credentials.length > 0
      ? credentials
      : credentialIds.map((id) => ({ id }));

  if (refs.length === 0) {
    throw new WebAuthnError("Nenhuma chave USB cadastrada.", "unknown");
  }

  const normalizedRefs = refs.map((ref) => ({
    id: normalizeStoredCredentialId(ref.id),
    transports: resolveUsbTransports(ref.transports),
  }));

  const allowCredentials: PublicKeyCredentialDescriptor[] = normalizedRefs.map(
    ({ id, transports }) => ({
      type: "public-key",
      id: base64urlToBuffer(id),
      transports,
    })
  );

  const publicKey: SecurityKeyRequestOptions = {
    challenge: randomChallenge(),
    rpId: getWebAuthnRpId(),
    allowCredentials,
    userVerification: "discouraged",
    timeout: WEBAUTHN_TIMEOUT_MS,
    hints: ["security-key"],
  };

  if (isDev()) {
    console.info("[WebAuthn:authenticate] local/mock ceremony", {
      rpId: publicKey.rpId,
      origin: window.location.origin,
      timeoutMs: WEBAUTHN_TIMEOUT_MS,
      credentialCount: normalizedRefs.length,
      hints: publicKey.hints,
      transports: normalizedRefs.map((ref) => ref.transports),
    });
  }

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      mediation: "required",
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (error) {
    logWebAuthnError("authenticate", error);
    throw error;
  }

  if (!assertion) {
    throw new WebAuthnError("Nenhuma credencial foi selecionada.", "unknown");
  }

  const credentialId = normalizeStoredCredentialId(
    bufferToBase64url(assertion.rawId)
  );
  const storedIds = normalizedRefs.map((ref) => ref.id);

  if (!storedIds.includes(credentialId)) {
    throw new WebAuthnError(
      "A credencial retornada não corresponde à chave USB cadastrada. Conecte a chave física correta ou entre com SMS.",
      "security"
    );
  }

  return { credentialId };
};

// ---------------------------------------------------------------------------
// Step-Up Authentication (server-backed only, falls back to regular auth)
// ---------------------------------------------------------------------------

export interface StepUpAuthParams {
  transactionId: string;
  amount?: number;
  credentials?: WebAuthnCredentialRef[];
}

export interface StepUpAuthResult {
  credentialId: string;
  success: boolean;
}

export const stepUpAuthenticate = async ({
  transactionId,
  amount,
  credentials = [],
}: StepUpAuthParams): Promise<StepUpAuthResult> => {
  assertWebAuthnAvailable();
  acquireCeremony("authenticate");

  try {
    if (!isMockMode()) {
      return await stepUpAuthenticateRemote({ transactionId, amount });
    }
    const result = await authenticateSecurityKeyLocal({ credentials });
    return { credentialId: result.credentialId, success: true };
  } finally {
    releaseCeremony();
  }
};

const stepUpAuthenticateRemote = async ({
  transactionId,
  amount,
}: Pick<StepUpAuthParams, "transactionId" | "amount">): Promise<StepUpAuthResult> => {
  const { webauthnApi } = await import("./webauthnApi");

  const { data: serverOptions } = await webauthnApi.getStepUpOptions({
    transaction_id: transactionId,
    amount,
  });
  const publicKey = decodeRequestOptions(serverOptions);
  (publicKey as SecurityKeyRequestOptions).hints = ["security-key"];
  publicKey.timeout = WEBAUTHN_TIMEOUT_MS;

  if (isDev()) {
    console.info("[WebAuthn:stepUp] remote ceremony", {
      transactionId,
      rpId: publicKey.rpId,
      origin: window.location.origin,
    });
    logDecodedCeremonyOptions("authenticate", publicKey);
  }

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      mediation: "required",
      publicKey,
    })) as PublicKeyCredential | null;
  } catch (error) {
    logWebAuthnError("authenticate", error);
    throw error;
  }

  if (!assertion) {
    throw new WebAuthnError("Nenhuma credencial foi selecionada.", "unknown");
  }

  const assertionPayload = serializeAssertionResponse(assertion);
  await webauthnApi.verifyStepUp(assertionPayload, transactionId);

  return {
    credentialId: normalizeStoredCredentialId(bufferToBase64url(assertion.rawId)),
    success: true,
  };
};
